import {
  DrugReferenceByConceptCode,
  DrugReferenceByConceptCodePayload
} from 'Interfaces/ReferenceInterfaces/DrugReferenceByConceptCodeInterface';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { drugReferenceByConceptCodeServices } from 'services/ReferenceScreenServices/drugReferenceByConceptCodeServices';

export class DrugReferenceByConceptCodeStore {
  drugReferenceByConceptCodes: DrugReferenceByConceptCode[] = [];
  isLoading: boolean = false;
  isPolling: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  searchQuery: string = '';
  selectedDrugReferenceByConceptCode: DrugReferenceByConceptCode | null = null;
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  // Initial load method (with loading indicator)
  fetchDrugReferenceByConceptCodes = async (page = 1, pageSize = 10000, search?: string) => {
    this.isLoading = true;
    try {
      const response = await drugReferenceByConceptCodeServices.getAllDrugReferenceByConceptCodes(
        page,
        pageSize,
        search
      );
      runInAction(() => {
        this.drugReferenceByConceptCodes = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
        if (search !== undefined) {
          this.searchQuery = search;
        }
      });
    } catch (error) {
      toast.error('Error fetching drug reference by concept codes');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Polling method (without loading indicator)
  pollDrugReferenceByConceptCodes = async (page = 1, pageSize = 10000, search?: string) => {
    this.isPolling = true;
    try {
      const response = await drugReferenceByConceptCodeServices.getAllDrugReferenceByConceptCodes(
        page,
        pageSize,
        search
      );
      runInAction(() => {
        // Update data only if it has changed
        if (JSON.stringify(this.drugReferenceByConceptCodes) !== JSON.stringify(response.data)) {
          this.drugReferenceByConceptCodes = response.data;
          this.pagination = {
            currentPage: response.pagination.current_page,
            totalPages: response.pagination.total_pages,
            totalItems: response.pagination.total_items,
            hasNext: response.pagination.has_next,
            hasPrevious: response.pagination.has_previous
          };
          if (search !== undefined) {
            this.searchQuery = search;
          }
        }
      });
    } catch (error) {
      // Don't show toast for polling errors to avoid spam
      if (this.environment === 'development') {
        console.error('Error polling drug reference by concept codes:', error);
      }
      toast.error('Error polling drug reference by concept codes!');
    } finally {
      runInAction(() => {
        this.isPolling = false;
      });
    }
  };

  // Method to refresh data (can be called manually)
  refreshData = async (page = 1, pageSize = 10000, search?: string) => {
    await this.fetchDrugReferenceByConceptCodes(page, pageSize, search);
  };

  createDrugReferenceByConceptCode = async (
    drugReferenceData: DrugReferenceByConceptCodePayload
  ): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response =
        await drugReferenceByConceptCodeServices.createDrugReferenceByConceptCode(
          drugReferenceData
        );
      if (response.status) {
        toast.success('Drug reference by concept code created successfully');
        await this.fetchDrugReferenceByConceptCodes(
          this.pagination.currentPage,
          10,
          this.searchQuery || undefined
        );
        return true;
      } else {
        toast.error(response.message || 'Failed to create drug reference by concept code');
        return false;
      }
    } catch (error) {
      toast.error('Error creating drug reference by concept code');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateDrugReferenceByConceptCode = async (
    id: number,
    drugReferenceData: DrugReferenceByConceptCodePayload
  ): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response = await drugReferenceByConceptCodeServices.updateDrugReferenceByConceptCode(
        id,
        drugReferenceData
      );
      if (response.status) {
        toast.success('Drug reference by concept code updated successfully');
        await this.fetchDrugReferenceByConceptCodes(
          this.pagination.currentPage,
          10,
          this.searchQuery || undefined
        );
        return true;
      } else {
        toast.error(response.message || 'Failed to update drug reference by concept code');
        return false;
      }
    } catch (error) {
      toast.error('Error updating drug reference by concept code');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteDrugReferenceByConceptCode = async (id: number): Promise<boolean> => {
    this.isLoading = true;
    try {
      const response =
        await drugReferenceByConceptCodeServices.deleteDrugReferenceByConceptCode(id);
      if (response.status) {
        toast.success('Drug reference by concept code deleted successfully');

        const isLastItemOnPage = this.drugReferenceByConceptCodes.length === 1;
        const isLastPage = this.pagination.currentPage === this.pagination.totalPages;

        if (isLastItemOnPage && !isLastPage && this.pagination.currentPage > 1) {
          await this.fetchDrugReferenceByConceptCodes(
            this.pagination.currentPage - 1,
            10,
            this.searchQuery || undefined
          );
        } else {
          await this.fetchDrugReferenceByConceptCodes(
            this.pagination.currentPage,
            10,
            this.searchQuery || undefined
          );
        }
        return true;
      } else {
        toast.error(response.message || 'Failed to delete drug reference by concept code');
        return false;
      }
    } catch (error) {
      toast.error('Error deleting drug reference by concept code');
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedDrugReferenceByConceptCode = (drugReference: DrugReferenceByConceptCode | null) => {
    this.selectedDrugReferenceByConceptCode = drugReference;
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };
}

export const drugReferenceByConceptCodeStore = new DrugReferenceByConceptCodeStore();
